/* eslint-disable import/prefer-default-export */
/**
 * Parser to generate Gen3 kaypair information out of a Gen2 private key
 *
 * <i>Copyright (c) 2019 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module key-parser.js
 * @class KeyParser
 * @since 0.0.1
 */

import Web3 from 'web3';

import keccak256 from 'keccak256';

import elliptic from 'elliptic';
// ecdsa: see https://www.npmjs.com/package/elliptic

// eslint-disable-next-line new-cap
const ec = new elliptic.ec('secp256k1');
const { utils } = Web3;
const REGEXP_ENODE =
	/^enode:\/\/([^@]+)@(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}):\d+$/;

/**
 * Generates an address out of a public key.
 *
 * @method publicKeyToAddress
 * @param publicKey {String} the public key
 * @return {String} the address generated by the public key
 * @since 0.4.0
 */
export const publicKeyToAddress = publicKey => {
	const pubBytes = utils.hexToBytes(`0x${publicKey}`);
	return `0x${keccak256(pubBytes).toString('hex').substr(24)}`;
};

/**
 * Parses the right properties out of an enode address, needed for the smartcontract masternode.announce.
 *
 * @method parseEnode
 * @param enode {String} an enode string representing a masternode
 * @return {Object} with the properties:
 * <ul>
 *      <li>masternodeAddress {String} --> raw properties</li>
 *      <li>ip4Address {String} --> ip4 address as a hex string</li>
 *      <li>enodeInfo {Array} --> enode as a 2 items bytes array</li>
 * </ul>
 * @since 0.4.0
 */
export const parseEnode = enode => {
	let parsed;
	let ecKey;
	let pubKeyArr33;
	let arr1;
	let arr2;
	let ips;
	let masternodeAddress;

	const match = enode.match(REGEXP_ENODE);
	const pubKey = match && match[1];
	const ip = match && match[2];

	if (pubKey && ip) {
		try {
			ips = ip.split('.');
			utils.bytesToHex(ips);
			ecKey = ec.keyFromPublic(`04${pubKey}`, 'hex');
			pubKeyArr33 = ecKey.getPublic().encodeCompressed();
			arr1 = pubKeyArr33.slice(0, 32);
			arr2 = pubKeyArr33.slice(32);
			while (arr2.length < 32) {
				arr2.push(0);
			}
			masternodeAddress = publicKeyToAddress(pubKey);
			parsed = {
				masternodeAddress,
				ip4Address: utils.bytesToHex(ips),
				enodeInfo: [arr1, arr2],
			};
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error(err);
			parsed = null;
		}
	}
	return parsed;
};
